// Invite lifecycle helpers shared by the org Team page and Super Admin.
//
// An invite is one of three things, and every list in the app splits on the
// same rule so "pending" means the same thing everywhere:
//   accepted — redeemed_at is set (someone signed up with it)
//   expired  — unredeemed and past expires_at (still resendable: a resend
//              restarts the clock on the same token, so the old link works)
//   pending  — unredeemed and still inside its window
//
// The TTL mirrors the org_invites.expires_at column default
// (now() + interval '7 days', 20260310165158) and the renewal window the
// send-invite-email edge function applies on resend.

export const INVITE_TTL_DAYS = 7;

export type InviteStatus = 'pending' | 'expired' | 'accepted';

export interface InviteLike {
  redeemed_at: string | null;
  expires_at: string;
}

export function inviteStatus(inv: InviteLike, now: Date = new Date()): InviteStatus {
  if (inv.redeemed_at) return 'accepted';
  return new Date(inv.expires_at) <= now ? 'expired' : 'pending';
}

/** Split invites into the two buckets the UI shows: open (pending + expired,
 *  everything nobody has accepted yet) and accepted. Order is preserved. */
export function partitionInvites<T extends InviteLike>(invites: T[], now: Date = new Date()) {
  const open: T[] = [];
  const accepted: T[] = [];
  for (const inv of invites) {
    (inviteStatus(inv, now) === 'accepted' ? accepted : open).push(inv);
  }
  return { open, accepted };
}

/** Unredeemed and still live — the number badges and headers count. */
export function countPending(invites: InviteLike[], now: Date = new Date()): number {
  return invites.filter(i => inviteStatus(i, now) === 'pending').length;
}
